// Hooks
import { useSelector } from "react-redux";
import usePhoto from "../usePhoto";
import useCustomers from "../useCustomers";
import { useHistory } from "react-router-dom";

// Actions
import LoadingAction from "@common/redux/actions/LoadingAction";
import LoginAction from "@common/redux/actions/LoginAction";
import PhotoAction from "@common/redux/actions/PhotoAction";
import AlertModalAction from "@common/redux/actions/AlertModalAction";
import CustomerReviewAction from "@common/redux/actions/CustomerReviewAction";
import CustomerReviewModalAction from "@common/redux/actions/CustomerReviewModalAction";
import CustomerInteractionAction from "@common/redux/actions/CustomerInteractionAction";
import CustomerFormAction from "@common/redux/actions/CustomerFormAction";
import ObservationsAction from "@common/redux/actions/ObservationsAction";

// Helpers
import { toggleStatusItem } from "./useReviewsFilters";
import { handleError } from "@common/components/helpers/error";
import { stripBase64Prefix } from "@common/components/helpers/image";
import {
  getDatePart,
  getSevenDaysInFuture,
} from "@common/components/helpers/datetime";

// API Clients
import CustomerClient from "@common/api/CustomerClient";
import useCustomerInteraction from "../useCustomerInteraction";
import { fetchInteractionOptions } from "../../helpers/CustomerInteraction/fetchInteractionOptions";
import { mapCustomersToOptions } from "../../helpers/Customer/customers";
import { mapStatusToCustomer } from "../../helpers/labels";

const useCustomerReviewModal = () => {
  const history = useHistory();
  const { reviewStatusCounts, reviewStatusIds } = useCustomers();

  const { photo } = usePhoto();

  const data = useSelector((state) =>
    CustomerReviewModalAction.getStatePath(state),
  );
  const { methods } = useCustomerInteraction({
    enabled: Boolean(data?.open),
  });
  const {
    selectedCustomer,
    viewingForm,
    comments,
    customerStatusOptions,
    selectedCustomerStatus,
    selectedSummaryRecord,
    returnDate,
  } = data;

  const setCustomer = (data) => CustomerFormAction.dispatch(data);

  const setCustomerInteractionState = (data) =>  CustomerInteractionAction.dispatch(data);
  const setObservationsState = (data) => ObservationsAction.dispatch(data);

  const setCustomerReviewState = CustomerReviewAction.dispatch;
  const setCustomerReviewModalState = CustomerReviewModalAction.dispatch;

  const getTabTitle = (currentTab) => {
    switch (currentTab) {
      case 1:
        return "Behaviour";
      case 2:
        return "Interactions";
      case 3:
        return "Linked Sessions";
      case 4:
        return "Reviews";
      case 5:
        return "History";
      case 6:
        return "Comments";
      default:
        return "";
    }
  };

  const onRecordClick = (selectedRecord) => {
    setCustomerReviewState({
      selectedRecord,
      selectedReview: selectedRecord,
    });
    setCustomerReviewModalState({ selectedRecord });
  };

  const onStatusChange = (id) => {
    const toggled = toggleStatusItem(id, reviewStatusIds);
    const newStatusIds =
      toggled.length > 0 ? toggled : reviewStatusCounts.map((x) => x.key);
    setCustomerReviewModalState({
      reviewStatusIds: newStatusIds,
    });
  };

  const onCustomerDetailsClick = (isInReadMode = false) => {
    LoginAction.dispatch({ isInReadMode }).then(() => {
      setCustomerReviewModalState({ openCustomerDetailsModal: true });
    });
  };

  const onRowClick = (record) => CustomerReviewModalAction.open(record);

  const closeModal = () => {
    if (viewingForm) {
      CustomerReviewModalAction.dispatch({
        viewingForm: !viewingForm,
      });
    } else {
      LoginAction.dispatch({ isInReadMode: false });
      PhotoAction.reset();
      CustomerReviewModalAction.dispatch({
        selectedRecord: null,
        selectedCustomer: null,
      });
      CustomerReviewModalAction.close();
    }
  };

  const onSave = () => {
    const image = photo ? stripBase64Prefix(photo) : selectedCustomer.image;
    const payload = {
      ...selectedCustomer,
      comments,
      image,
      ...toPayload(selectedCustomerStatus),
      returnDate: getDatePart(returnDate),
    };

    LoadingAction.set(true).then(() =>
      CustomerClient.Update(payload)
        .then((res) => {
          const updatedSelectedCustomer = {
            ...payload,
            rowVersion: res?.rowVersion,
            returnDate: res?.returnDate,
          };
          AlertModalAction.open("Customer updated!", "Success")
          setCustomerReviewModalState({
            selectedCustomer: updatedSelectedCustomer,
          });
          if (selectedCustomerStatus?.value === "isTakingABreak") {
            const dateToSubmit =
            returnDate || getDatePart(getSevenDaysInFuture());
            setCustomerReviewModalState({
              selectedCustomer: updatedSelectedCustomer,
              customerStatus: selectedCustomerStatus,
              returnDate: dateToSubmit,
            });
          } else {
            setCustomerReviewModalState({
              selectedCustomer: updatedSelectedCustomer,
              customerStatus: selectedCustomerStatus,
              returnDate: null,
            });
          }
        })
        .catch((error) => {
          handleError(error);
          setCustomerReviewModalState({ selectedCustomerStatus: null });
        })
        .finally(() => {
          LoadingAction.set(false);
          setCustomerReviewModalState({ openStatusChangeModal: false });
        }),
    );
  };

  const STATUS_KEYS = customerStatusOptions.map((option) => option.value);

  const toPayload = (selected) => {
    if (!selected) return null;
    return STATUS_KEYS.reduce((acc, key) => {
      acc[key] = key === selected.value;
      return acc;
    }, {});
  };

  const onCommentsChange = (event) =>
    setCustomerReviewModalState({ comments: event.target.value });

  const onCustomerStatusChange = (_, { name, value }) => {
    const selectedCustomerStatus = customerStatusOptions.find(
      (x) => x.value === value,
    );

    // const updatedCustomer = mapStatusToCustomer({
    //   customerStatus: selectedCustomerStatus,
    //   customer: selectedSummaryRecord?.customer,
    // });

    setCustomerReviewModalState({
      selectedCustomerStatus,
      openStatusChangeModal: true,
      selectedSummaryRecord: {
        ...selectedSummaryRecord,
        // customer: updatedCustomer,
      },
    });
  };

  const name = selectedCustomer
    ? `${selectedCustomer.forename ?? ""} ${selectedCustomer.surname ?? ""}`
    : "Customer Interaction";

  const changeTab = (currentTab) => setCustomerReviewModalState({ currentTab });

  const toggleCustomerReviewModal = (value) =>
    setCustomerReviewModalState({ openCustomerReviewModal: value });

  const onNewInteractionClick = (customer) => {
    const parsedCustomer = mapCustomersToOptions([customer]);
    closeModal();
    setCustomer({ customers: parsedCustomer });
    setCustomerInteractionState({
      isCustomerVulnerable: true,
      hideCustomerSearch: true,
      customer: customer,
      customerId: customer?.id,
      searchedCustomer: customer?.id,
    });
    fetchInteractionOptions();
    history.push("/compliance/new-customer-interaction");
  };

  const onNewObservationClick = (customer) => {
    const parsedCustomers = mapCustomersToOptions([customer]);
    closeModal();
    setCustomer({ customers: parsedCustomers });
    setObservationsState({
      customer: customer,
      searchedCustomer: customer.id,
      hideCustomerSearch: true,
      customerUnknown: false,
      customerId: customer?.id,
    });
    fetchInteractionOptions();
    history.push("/compliance/observations")
  };

  return {
    ...data,
    getTabTitle,
    reviewStatusIds,
    methods,
    setCustomerReviewModalState,
    onStatusChange,
    onRowClick,
    onRecordClick,
    closeModal,
    onCustomerDetailsClick,
    name,
    onSave,
    onCommentsChange,
    onCustomerStatusChange,
    changeTab,
    toggleCustomerReviewModal,
    onNewInteractionClick,
    onNewObservationClick,
  };
};

export default useCustomerReviewModal;
